import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type {
  ComposerResponse, ComposerStatus, ComposerTurn,
} from '../../components/Workflows/composerTypes';
import type { Graph, Workflow } from '../../components/Workflows/api';
import { createWorkflow as apiCreate, updateWorkflow as apiUpdate } from '../../components/Workflows/api';
import { applyPatchToGraph } from '../../components/Workflows/composerPatch';
import { getClerkToken, withWsAuth, WS_AUTH_REJECTED_CODE } from '../../auth/clerk';

const genId = (prefix: string) =>
  `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const emptyGraph = (): Graph => ({
  nodes: [{ id: 'n_start', type: 'start.trigger', config: {}, position: { x: 80, y: 200 } }],
  edges: [],
  trigger: null,
  docs_url: null,
});

export type ComposerMode = 'new' | 'edit';

export interface WorkflowComposerSlice {
  // Session
  composerOpen: boolean;
  composerMode: ComposerMode;
  composerStatus: ComposerStatus;
  composerWorkflowId: string | null;  // set when mode === 'edit'
  composerName: string;                // workflow name being composed
  composerGraph: Graph;                // current draft graph (before applying pending patch)
  composerOriginalGraphJson: string;   // snapshot on start, used to detect divergence
  composerTurns: ComposerTurn[];
  composerPendingResponse: ComposerResponse | null;  // most recent response awaiting user action
  composerError: string | null;
  composerSessionId: string | null;
  composerThreadId: string | null;  // stable id per composer session; reused across turns

  // Actions
  composerStart: (mode: ComposerMode, initialName?: string, existing?: Workflow | null) => void;
  composerClose: () => void;
  composerSendMessage: (text: string) => void;
  composerAnswerClarification: (answers: { id: string; prompt: string; answer: string }[]) => void;
  composerApplyPatch: () => void;
  composerDismissPatch: () => void;
  composerFinalize: () => Promise<string | null>;
}

// ---------- WS singleton ---------- //

let _ws: WebSocket | null = null;
/** Whether this connection ever heard back — see the note in ensureWs. */
let _wsProgressed = false;
let _wsRetried = false;
let _sendQueue: string[] = [];
/** Payloads handed to a socket that has not answered yet, so a reconnect can requeue them. */
let _inFlight: string[] = [];
/**
 * Which composer session the socket belongs to. A reconnect waits on a token
 * refresh with `_ws` already null, so composerStart/composerClose have nothing
 * to tear down during that window and the pending reconnect would otherwise
 * drain a stale prompt into a closed or replaced session.
 */
let _wsGen = 0;

/** Drop the socket and everything queued for it. Invalidates pending reconnects. */
function resetComposerWs(): void {
  _wsGen += 1;
  if (_ws) { try { _ws.close(); } catch {} _ws = null; }
  _sendQueue = [];
  _inFlight = [];
}

function wsUrl(): string {
  const envUrl = process.env.REACT_APP_COMPOSER_WS_URL;
  if (envUrl) return withWsAuth(envUrl);
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname;
  // CRA dev server runs on 3000; backend on 5001. In prod assume same origin.
  const port = window.location.port === '3000' ? '5001' : window.location.port;
  return withWsAuth(`${proto}//${host}${port ? ':' + port : ''}/api/workflow-composer/ws`);
}

function ensureWs(set: (partial: any) => void, onResponse: (resp: ComposerResponse) => void, onStatus: (s: ComposerStatus) => void, onSession: (sid: string) => void): WebSocket {
  if (_ws && _ws.readyState === WebSocket.OPEN) return _ws;
  if (_ws && _ws.readyState === WebSocket.CONNECTING) return _ws;

  // A handshake the backend rejects (an expired Clerk session token → 4401)
  // delivers nothing. The token cache is read synchronously at connect time
  // and cannot be awaited, so reconnecting once with a freshly minted token is
  // what recovers a stale one.
  _wsRetried = false;
  const myGen = _wsGen;

  const connect = (): WebSocket => {
    const ws = new WebSocket(wsUrl());
    _ws = ws;
    _wsProgressed = false;
    onStatus('connecting');

    ws.onopen = () => {
      // Sent, but not yet acknowledged. A rejected handshake is ACCEPTED
      // before it is closed, so this drains into a socket that is already
      // doomed — hold the payloads until the server answers, or the retry
      // reconnects to an empty queue and the user's prompt is simply gone.
      while (_sendQueue.length) {
        const payload = _sendQueue.shift();
        if (payload) { _inFlight.push(payload); ws.send(payload); }
      }
    };
    ws.onmessage = (ev) => {
      _wsProgressed = true;
      _inFlight = [];
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'session' && data.session_id) {
          onSession(data.session_id);
          return;
        }
        if (data.type === 'status') {
          onStatus(data.status === 'thinking' ? 'thinking' : 'idle');
          return;
        }
        if (data.type === 'error') {
          onStatus('error');
          set({ composerError: data.error || 'unknown error' });
          return;
        }
        if (data.type === 'response') {
          const resp = data as ComposerResponse;
          onResponse(resp);
        }
      } catch (e) {
        // ignore
      }
    };
    ws.onclose = (e) => {
      // Replaced or torn down by composerStart/composerClose, both of which
      // null the singleton first — that path owns the state now.
      if (_ws !== ws) return;
      _ws = null;
      if (_wsProgressed) { _sendQueue = []; _inFlight = []; return; }

      // Nothing came back, so nothing has been acted on and one reconnect is
      // always worth trying. Keyed on silence rather than on whether the
      // socket opened: the backend accepts a rejected handshake before closing
      // it, so `onopen` fires for exactly the case the retry exists to fix.
      if (myGen !== _wsGen) { _inFlight = []; return; }
      if (!_wsRetried) {
        _wsRetried = true;
        // The prompt the user just typed goes back on the queue rather than
        // being dropped — otherwise a recoverable handshake becomes a lost turn.
        _sendQueue = [..._inFlight, ..._sendQueue];
        _inFlight = [];
        const reconnect = () => { if (myGen === _wsGen) connect(); };
        getClerkToken().then(reconnect, reconnect);
        return;
      }
      onStatus('error');
      set({
        composerError: e.code === WS_AUTH_REJECTED_CODE
          ? 'Your session expired — please reload and try again'
          : 'Could not reach the composer — please retry',
      });
      _sendQueue = [];
      _inFlight = [];
    };
    ws.onerror = () => {
      // A socket that never delivered anything is left to onclose, the only
      // place that can tell a rejected token from an unreachable server.
      if (_wsProgressed) set({ composerError: 'Connection error — please retry' });
    };

    return ws;
  };

  return connect();
}

function wsSend(ws: WebSocket, payload: any) {
  const s = JSON.stringify(payload);
  if (ws.readyState === WebSocket.OPEN) { _inFlight.push(s); ws.send(s); }
  else _sendQueue.push(s);
}

// ---------- Slice ---------- //

export const createWorkflowComposerSlice: StateCreator<
  VoyagerState, [], [], WorkflowComposerSlice
> = (set, get) => ({
  composerOpen: false,
  composerMode: 'new',
  composerStatus: 'idle',
  composerWorkflowId: null,
  composerName: '',
  composerGraph: emptyGraph(),
  composerOriginalGraphJson: '',
  composerTurns: [],
  composerPendingResponse: null,
  composerError: null,
  composerSessionId: null,
  composerThreadId: null,

  composerStart: (mode, initialName, existing) => {
    // Reset session state
    resetComposerWs();
    const graph: Graph = existing?.steps || emptyGraph();
    // Ensure start node exists even on edit mode
    const hasStart = graph.nodes.some((n) => n.type === 'start.trigger');
    const normalizedGraph: Graph = hasStart ? graph : {
      ...graph,
      nodes: [{ id: 'n_start', type: 'start.trigger', config: {}, position: { x: 80, y: 200 } }, ...graph.nodes],
    };
    // Clear any selection from the editor so the inspector isn't stuck on
    // a node id that doesn't exist in the draft graph.
    get().wfSelectNode(null);
    set({
      composerOpen: true,
      composerMode: mode,
      composerStatus: 'idle',
      composerWorkflowId: existing?.id || null,
      composerName: existing?.name || initialName || 'New Workflow',
      composerGraph: normalizedGraph,
      composerOriginalGraphJson: JSON.stringify(normalizedGraph),
      composerTurns: [],
      composerPendingResponse: null,
      composerError: null,
      composerSessionId: null,
      composerThreadId: genId('thr'),
    });
  },

  composerClose: () => {
    const s = get();

    // Nudge 1: if a response is sitting in the pane waiting for the user to
    // act on it, don't let them flip away silently. Surface it in the pane
    // instead of closing.
    const pending = s.composerPendingResponse;
    if (pending) {
      if (pending.kind === 'clarification') {
        set({ composerError: 'Answer the agent\'s question or dismiss it before switching to manual.' });
      } else if (pending.kind === 'patch' || pending.kind === 'done') {
        set({ composerError: 'Apply or dismiss the pending changes before switching to manual.' });
      }
      return;
    }

    // Nudge 2: if the draft diverged from the starting graph, confirm
    // before discarding. Only applies in edit mode (new-mode drafts have
    // no saved counterpart).
    const draftChanged =
      s.composerMode === 'edit' &&
      s.composerOriginalGraphJson &&
      s.composerOriginalGraphJson !== JSON.stringify(s.composerGraph);
    if (draftChanged) {
      const ok = window.confirm('Discard AI draft? Any changes not yet saved will be lost.');
      if (!ok) return;
    }

    resetComposerWs();
    set({
      composerOpen: false,
      composerStatus: 'idle',
      composerTurns: [],
      composerPendingResponse: null,
      composerError: null,
      composerThreadId: null,
    });
  },

  composerSendMessage: (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const turnId = genId('t');
    const turn: ComposerTurn = {
      id: turnId,
      userText: trimmed,
      sentAt: Date.now(),
      status: 'thinking',
      response: null,
    };
    set((s) => ({
      composerTurns: [...s.composerTurns, turn],
      composerStatus: 'thinking',
      composerPendingResponse: null,
      composerError: null,
    }));

    const handleResponse = (resp: ComposerResponse) => {
      const st = get();

      // Frontend-side empty-patch guard. Even though the agent tries to
      // downgrade these to "message", anything can slip through (bad JSON,
      // sanitizer-only removals, etc.). Treat an all-empty patch as "no
      // actionable change" so we don't render a useless Apply button.
      const isEmptyPatch = (p: any) => !p || !(
        (p.add_nodes && p.add_nodes.length) ||
        (p.add_edges && p.add_edges.length) ||
        (p.update_nodes && p.update_nodes.length) ||
        (p.remove_node_ids && p.remove_node_ids.length) ||
        (p.remove_edge_ids && p.remove_edge_ids.length) ||
        p.set_trigger !== undefined && p.set_trigger !== null ||
        p.set_docs_url !== undefined && p.set_docs_url !== null ||
        p.set_name !== undefined && p.set_name !== null
      );
      let effectiveResp = resp;
      if ((resp.kind === 'patch' || resp.kind === 'done') && isEmptyPatch(resp.patch)) {
        // eslint-disable-next-line no-console
        console.warn('[composer] received empty patch; downgrading to message', resp);
        effectiveResp = { ...resp, kind: 'message', patch: null };
      }

      const next: ComposerStatus =
        effectiveResp.kind === 'error' ? 'error'
        : effectiveResp.kind === 'clarification' ? 'clarifying'
        : effectiveResp.kind === 'done' ? (effectiveResp.patch ? 'awaiting_apply' : 'done')
        : effectiveResp.kind === 'patch' ? 'awaiting_apply'
        : 'idle';
      set({
        composerTurns: st.composerTurns.map((t) =>
          t.id === turnId ? { ...t, status: next, response: effectiveResp } : t
        ),
        composerStatus: next,
        composerPendingResponse:
          (effectiveResp.kind === 'patch' || (effectiveResp.kind === 'done' && effectiveResp.patch))
            ? effectiveResp
            : effectiveResp.kind === 'clarification' ? effectiveResp : null,
      });
    };

    const handleStatus = (s: ComposerStatus) => {
      set((st) => ({
        composerStatus: s === 'connecting' ? 'connecting' : s,
        // A turn only ever left 'thinking' on a response, so an error — from
        // the server, or from a connection that never delivered one — left the
        // prompt's spinner running beside the error text. Settle by state
        // rather than by turn id: only one turn is ever in flight, and the
        // socket's handlers may belong to an earlier send.
        composerTurns: s === 'error'
          ? st.composerTurns.map((t) => (
            t.status === 'thinking' ? { ...t, status: 'error' as const } : t
          ))
          : st.composerTurns,
      }));
    };

    const handleSession = (sid: string) => set({ composerSessionId: sid });

    const ws = ensureWs(set, handleResponse, handleStatus, handleSession);
    const s = get();
    // Send current graph + turn history (user+agent, last few)
    const history = s.composerTurns
      .filter((t) => t.response)
      .flatMap((t) => [
        { role: 'user', content: t.userText },
        { role: 'agent', content: t.response?.message || '' },
      ]);

    wsSend(ws, {
      type: 'user_message',
      query: trimmed,
      context: {
        workflow_id: s.composerWorkflowId,
        name: s.composerName,
        graph: s.composerGraph,
      },
      history,
      thread_id: s.composerThreadId,
    });
  },

  composerAnswerClarification: (answers) => {
    // Flatten answers to a single message.
    const lines = answers.map((a) => `- ${a.prompt.replace(/\n/g, ' ')}: ${a.answer}`);
    const text = `Answers:\n${lines.join('\n')}`;
    get().composerSendMessage(text);
  },

  composerApplyPatch: () => {
    const { composerPendingResponse, composerGraph } = get();
    if (!composerPendingResponse || !composerPendingResponse.patch) return;
    const patch = composerPendingResponse.patch;

    const nextGraph = applyPatchToGraph(composerGraph, patch);
    const nextName = patch.set_name || get().composerName;
    // Diagnostic: surface what the agent actually emitted vs what landed.
    // Helps catch cases where the sanitizer drops nodes or edges reference
    // unknown ids. Safe to keep — no PII, no sensitive payloads.
    // eslint-disable-next-line no-console
    console.debug('[composer] applying patch', {
      add_nodes: (patch.add_nodes || []).map((n) => ({ id: n.id, type: n.type })),
      add_edges: (patch.add_edges || []).map((e) => ({ source: e.source, target: e.target })),
      update_nodes: (patch.update_nodes || []).map((u) => u.id),
      remove_node_ids: patch.remove_node_ids || [],
      before_nodes: composerGraph.nodes.length,
      before_edges: composerGraph.edges.length,
      after_nodes: nextGraph.nodes.length,
      after_edges: nextGraph.edges.length,
    });
    // Node ids may have shifted; clear selection so the inspector isn't
    // stuck on a node that no longer exists.
    get().wfSelectNode(null);
    set({
      composerGraph: nextGraph,
      composerName: nextName,
      composerPendingResponse: null,
      composerError: null,
      composerStatus: composerPendingResponse.kind === 'done' ? 'done' : 'idle',
    });
  },

  composerDismissPatch: () => {
    set({
      composerPendingResponse: null,
      composerError: null,
      composerStatus: 'idle',
    });
  },

  composerFinalize: async () => {
    const s = get();
    try {
      if (s.composerMode === 'edit' && s.composerWorkflowId) {
        await apiUpdate(s.composerWorkflowId, { name: s.composerName, steps: s.composerGraph });
        // Refresh editor. The draft is now committed, so clear divergence
        // tracking and bypass the discard-confirm path inside composerClose.
        await get().wfOpen(s.composerWorkflowId);
        set({ composerOriginalGraphJson: JSON.stringify(s.composerGraph) });
        get().composerClose();
        return s.composerWorkflowId;
      }
      const created = await apiCreate({ name: s.composerName, steps: s.composerGraph });
      await get().wfOpen(created.id);
      await get().wfLoadList();
      set({ composerOriginalGraphJson: JSON.stringify(s.composerGraph) });
      get().composerClose();
      return created.id;
    } catch (e: any) {
      set({ composerError: e?.message || 'Failed to save workflow' });
      return null;
    }
  },
});
